import React, { useState } from 'react';
import {
  NavbarWrapper,
  NavbarContainer,
  BrandSection,
  RightSection,
  UserMenu,
  UserMenuTrigger,
  UserMenuDropdown,
  UserMenuHeader,
  UserName,
  UserRole,
  Divider,
  MenuItemButton,
  LogoutButton,
} from './navbar.styles';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  DarkModeIcon,
  LightModeIcon,
  Logo,
  ThemeToggleButton,
} from '../../GeneralPages/landingPages/components/shared.styles';
import imgUrbanDark from '../../../assets/PNG/urbanCompass_dark.png';
import imgUrbanLight from '../../../assets/PNG/urbanCompass_light.png';
import { useAuth } from '../../../hooks/useAuth';
import ChangePasswordModal from './modals/changePasswordModal';
import UpdateEmailModal from './modals/updateEmailModal';
import VerificationEmailModal from './modals/verificationEmailModal';
import { Mail, MailWarning } from 'lucide-react';

const Navbar = ({ isDark = true, toggleTheme }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isUpdateEmailOpen, setIsUpdateEmailOpen] = useState(false);
  const [isVerifyEmailOpen, setIsVerifyEmailOpen] = useState(false);
  const { userData, logout, councilInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoginPage = location.pathname === '/login';

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuClick = (action) => {
    if (action === 'changePassword') {
      setIsChangePasswordOpen(true);
      setIsMenuOpen(false);
      return;
    }

    if (action === 'updateEmail') {
      setIsUpdateEmailOpen(true);
      setIsMenuOpen(false);
      return;
    }

    logout();
    setIsMenuOpen(false);
    navigate('/');
  };

  return (
    <NavbarWrapper>
      <NavbarContainer>
        <BrandSection>
          <Link to="/">
            {isDark ? (
              <Logo src={councilInfo?.darkModeLogo?.url ?? imgUrbanDark} alt="logo" />
            ) : (
              <Logo src={councilInfo?.lightModeLogo?.url ?? imgUrbanLight} alt="logo" />
            )}
          </Link>
        </BrandSection>

        <RightSection>
          <ThemeToggleButton onClick={toggleTheme}>
            <LightModeIcon
              $isDark={isDark}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 -960 960 960"
            >
              <path d="M480-360q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35Zm0 80q-83 0-141.5-58.5T280-480q0-83 58.5-141.5T480-680q83 0 141.5 58.5T680-480q0 83-58.5 141.5T480-280ZM200-440H40v-80h160v80Zm720 0H760v-80h160v80ZM440-760v-160h80v160h-80Zm0 720v-160h80v160h-80ZM256-650l-101-97 57-59 96 100-52 56Zm492 496-97-101 53-55 101 97-57 59Zm-98-550 97-101 59 57-100 96-56-52ZM154-212l101-97 55 53-97 101-59-57Zm326-268Z" />
            </LightModeIcon>
            <DarkModeIcon
              $isDark={isDark}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 -960 960 960"
            >
              <path d="M480-120q-150 0-255-105T120-480q0-150 105-255t255-105q14 0 27.5 1t26.5 3q-41 29-65.5 75.5T444-660q0 90 63 153t153 63q55 0 101-24.5t75-65.5q2 13 3 26.5t1 27.5q0 150-105 255T480-120Zm0-80q88 0 158-48.5T740-375q-20 5-40 8t-40 3q-123 0-209.5-86.5T364-660q0-20 3-40t8-40q-78 32-126.5 102T200-480q0 116 82 198t198 82Zm-10-270Z" />
            </DarkModeIcon>
          </ThemeToggleButton>

          {!isLoginPage && (
            <UserMenu>
              <UserMenuTrigger onClick={toggleMenu}>
                <div
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '50%',
                    background: '#4F46E5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                  }}
                >
                  {userData?.username
                    ? userData?.username
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                    : 'MG'}
                </div>
              </UserMenuTrigger>

              {isMenuOpen && (
                <UserMenuDropdown>
                  <UserMenuHeader>
                    <UserName>{userData?.username}</UserName>
                    <UserRole>{userData?.userType}</UserRole>
                    <UserRole>{userData?.email}</UserRole>
                  </UserMenuHeader>

                  <Divider />

                  {!isLoginPage && userData?.emailVerified === false && (
                    <MenuItemButton onClick={() => setIsVerifyEmailOpen(true)}>
                      <MailWarning size={18} color="#f59e0b" />
                      Email Verification
                    </MenuItemButton>
                  )}

                  <MenuItemButton onClick={() => handleMenuClick('changePassword')}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M12 7V5C12 2.79 10.21 1 8 1C5.79 1 4 2.79 4 5V7C3.45 7 3 7.45 3 8V14C3 14.55 3.45 15 4 15H12C12.55 15 13 14.55 13 14V8C13 7.45 12.55 7 12 7ZM8 11.5C7.17 11.5 6.5 10.83 6.5 10C6.5 9.17 7.17 8.5 8 8.5C8.83 8.5 9.5 9.17 9.5 10C9.5 10.83 8.83 11.5 8 11.5ZM10.5 7H5.5V5C5.5 3.62 6.62 2.5 8 2.5C9.38 2.5 10.5 3.62 10.5 5V7Z"
                        fill="currentColor"
                      />
                    </svg>
                    Change password
                  </MenuItemButton>

                  <MenuItemButton onClick={() => handleMenuClick('updateEmail')}>
                    <Mail />
                    Update email
                  </MenuItemButton>

                  <Divider />

                  <LogoutButton onClick={() => handleMenuClick('logout')}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M10 13H3V3H10V1H3C1.9 1 1 1.9 1 3V13C1 14.1 1.9 15 3 15H10V13ZM12.17 7.83L9.59 5.25L10.88 3.96L15 8L10.88 12.12L9.59 10.83L12.17 8.25H5V6.75H12.17V7.83Z"
                        fill="currentColor"
                      />
                    </svg>
                    Logout
                  </LogoutButton>
                </UserMenuDropdown>
              )}
            </UserMenu>
          )}
        </RightSection>
      </NavbarContainer>

      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />

      <UpdateEmailModal isOpen={isUpdateEmailOpen} onClose={() => setIsUpdateEmailOpen(false)} />

      <VerificationEmailModal
        isOpen={isVerifyEmailOpen}
        onClose={() => setIsVerifyEmailOpen(false)}
        email={userData?.email}
      />
    </NavbarWrapper>
  );
};

export default Navbar;
